import jwt_decode from 'jwt-decode';
import { createContext, FCWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { UserManager, User } from 'oidc-client-ts';
import { IEnvSettings } from './authSettings';
import { useAppDispatch } from '../../app/hooks';
import {
    setAuthToken,
    saveTenantId,
    saveUserId,
} from './authSlice';

export const authRedirectPath = '/auth-callback';
export const silentAuthRedirectPath = '/silent-renew-callback';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const AuthContext = createContext<UserManager>(undefined!);
export const UserSessionContext = createContext<User | undefined | null>(null);
export const TokenContext = createContext('token_context_not_initialized');

const AuthServiceProvider: FCWithChildren<{ opts: IEnvSettings }> = ({ opts, children }) => {

    const { authority, clientId, scope } = opts;
    const [user, setUser] = useState<User | null>();
    const dispatch = useAppDispatch();

    const updateUser = useCallback(
        (newUser?: User | null) => {
            if (newUser?.access_token) {
                dispatch(setAuthToken(newUser.access_token));
                dispatch(saveTenantId(jwt_decode<{ tenant: string }>(newUser.access_token).tenant));
                dispatch(saveUserId(newUser.profile.sub));
            }
            setUser(newUser);
        },
        [dispatch],
    );

    const userManager = useMemo(
        () => {
            const acrValues: string[] = [];

            const tenantId = localStorage.getItem('selectedTenantId');
            if (tenantId)
                acrValues.push(`tenant:${tenantId}`);

            const isSilentLogin = !!sessionStorage.getItem('isSilentLogin');
            if (isSilentLogin)
                acrValues.push('idp:aad.oidc');

            return new UserManager({
                authority: authority,
                client_id: clientId,
                redirect_uri: `${window.location.origin}${authRedirectPath}`,
                post_logout_redirect_uri: window.location.origin,
                acr_values: (acrValues.length > 0) ? acrValues.join(' ') : undefined,
                response_type: 'code',
                scope: scope,
                loadUserInfo: true,
                silent_redirect_uri: `${window.location.origin}${silentAuthRedirectPath}`,
                accessTokenExpiringNotificationTimeInSeconds: 300,
                automaticSilentRenew: true
            });
        },
        [authority, clientId, scope],
    );

    useEffect(
        () => {
            userManager.getUser().then(updateUser);
            userManager.events.addUserLoaded(updateUser);
            userManager.events.addSilentRenewError((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                userManager.signinRedirect();
            });
            return () => userManager.events.removeUserLoaded(updateUser);
        },
        [updateUser, userManager],
    );

    return <AuthContext.Provider value={userManager}>
                <UserSessionContext.Provider value={user}>
                    {children}
                </UserSessionContext.Provider>
            </AuthContext.Provider>;

};

export default AuthServiceProvider;